import { defineStore } from 'pinia'
import type {
  ClientImageCloudInitDto,
  ClientImageCloudInitValidationDto,
  ClientImageDto,
  ClientImageQuotaDto,
  ClientImageTargetDto,
  HardwareOptionsResponseDto,
  GetVmHardwareConfig200Response,
  UpdateVmHardwareDto,
  UpdateClientImageDto,
} from '~/generated-api-client/api'

export type {
  ClientImageDto,
  ClientImageQuotaDto,
  ClientImageTargetDto,
  ClientImageCloudInitDto,
  ClientImageCloudInitValidationDto,
}

/** Statuses where the artifact is still being worked on — the page polls these. */
export const IN_PROGRESS_STATUSES = [
  'awaiting_upload',
  'uploading',
  'verifying',
  'converting',
  'processing',
]

export function isImageBusy(image: ClientImageDto): boolean {
  return IN_PROGRESS_STATUSES.includes(image.status)
}

export const useImagesStore = defineStore('images', () => {
  const images = ref<ClientImageDto[]>([])
  const quota = ref<ClientImageQuotaDto | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  function api() {
    const { $mwApi } = useNuxtApp()
    return $mwApi as any
  }

  const isos = computed(() => images.value.filter((i) => i.kind === 'iso'))
  const diskImages = computed(() => images.value.filter((i) => i.kind === 'disk_image'))
  /** Anything still uploading or processing, which is what drives the poll. */
  const busy = computed(() => images.value.filter(isImageBusy))

  /**
   * Which ISO is in each instance's drive.
   *
   * Keyed by VM so the instance page can answer "is something mounted here?"
   * without a request of its own.
   */
  const mountedByVm = computed<Record<number, ClientImageDto>>(() => {
    const map: Record<number, ClientImageDto> = {}
    for (const image of images.value) {
      for (const mount of image.mounts ?? []) map[mount.vm_id] = image
    }
    return map
  })

  async function fetchImages(): Promise<ClientImageDto[]> {
    loading.value = true
    error.value = null
    try {
      const res = await api().listClientImages()
      images.value = res.data
      return res.data
    } catch (err: any) {
      error.value = err?.response?.data?.message ?? err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /** Refresh without the loading flag, for the background poll. */
  async function refreshQuietly(): Promise<void> {
    try {
      const res = await api().listClientImages()
      images.value = res.data
    } catch {
      // A failed poll is not worth surfacing; the next tick tries again.
    }
  }

  async function fetchQuota(): Promise<ClientImageQuotaDto | null> {
    try {
      const res = await api().getClientImageQuota()
      quota.value = res.data
      return res.data
    } catch {
      return null
    }
  }

  async function getImage(imageId: string): Promise<ClientImageDto> {
    const res = await api().getClientImage(imageId)
    return res.data
  }

  async function updateImage(
    imageId: string,
    payload: UpdateClientImageDto,
  ): Promise<ClientImageDto> {
    const res = await api().updateClientImage(imageId, payload)
    const updated = res.data as ClientImageDto
    const i = images.value.findIndex((img) => img.id === imageId)
    if (i >= 0) images.value[i] = updated
    return updated
  }

  // ── Cloud-init user data ─────────────────────────────────────────────
  //
  // The customer's HALF of cloud-init. Never in the list response — the
  // document can be 256 KiB, and a library page has no use for it.

  async function getCloudInit(imageId: string): Promise<ClientImageCloudInitDto> {
    const res = await api().getClientImageCloudInit(imageId)
    return res.data
  }

  async function updateCloudInit(
    imageId: string,
    userData: string | null,
  ): Promise<ClientImageCloudInitDto> {
    const res = await api().updateClientImageCloudInit(imageId, {
      user_data: userData,
    })
    // The summary on the row (format, changed-at) moved, and the card and the
    // checkout picker both read it.
    await getImage(imageId).then((updated) => {
      const i = images.value.findIndex((img) => img.id === imageId)
      if (i >= 0) images.value[i] = updated
    })
    return res.data
  }

  async function validateCloudInit(
    imageId: string,
    userData: string,
    family?: 'cloud-init' | 'cloudbase-init',
  ): Promise<ClientImageCloudInitValidationDto> {
    const res = await api().validateClientImageCloudInit(imageId, {
      user_data: userData,
      ...(family ? { family } : {}),
    })
    return res.data
  }

  async function renewImage(imageId: string): Promise<ClientImageDto> {
    const res = await api().renewClientImage(imageId)
    const updated = res.data as ClientImageDto
    const i = images.value.findIndex((img) => img.id === imageId)
    if (i >= 0) images.value[i] = updated
    return updated
  }

  async function deleteImage(imageId: string): Promise<void> {
    await api().deleteClientImage(imageId)
    images.value = images.value.filter((img) => img.id !== imageId)
  }

  async function getTargets(imageId: string): Promise<ClientImageTargetDto[]> {
    const res = await api().getClientImageTargets(imageId)
    return res.data?.targets ?? []
  }

  async function attach(imageId: string, vmId: number): Promise<string> {
    const res = await api().attachClientImage(imageId, vmId)
    await fetchImages()
    return res.data?.message ?? ''
  }

  async function detach(imageId: string, vmId: number): Promise<string> {
    const res = await api().detachClientImage(imageId, vmId)
    await fetchImages()
    return res.data?.message ?? ''
  }

  async function reinstallFromImage(vmId: number, imageId: string): Promise<void> {
    await api().deployVmFromClientImage(imageId, vmId)
  }

  // VM hardware endpoints, used by the attach flow and the instance page. They
  // belong to `vm-hardware`, not to images, but every caller reaches them from
  // an image context and a second store for three passthroughs earns nothing.
  async function getHardwareOptions(): Promise<HardwareOptionsResponseDto> {
    // `getVmHardwareOptions`, not `getHardwareOptions` — the generated name
    // carries the controller prefix. The wrong one threw, the caller's
    // `.catch` swallowed it, and the attach dialog simply rendered without its
    // hardware fields with nothing anywhere saying why.
    const res = await api().getVmHardwareOptions()
    return res.data
  }

  async function getVmHardwareConfig(vmId: number): Promise<GetVmHardwareConfig200Response> {
    const res = await api().getVmHardwareConfig(vmId)
    return res.data
  }

  /**
   * Apply a hardware change to an instance.
   *
   * The payload is widened to plain strings and cast at the boundary: the
   * generated client narrows these to enums, but the values come from
   * `GET /vm-hardware/options`, which the API builds at runtime. Typing the
   * caller against the generated enum would mean the option list and the type
   * could disagree with nothing to reconcile them.
   */
  async function updateVmHardware(
    vmId: number,
    payload: { diskInterface?: string; nicModel?: string; osType?: string },
  ): Promise<unknown> {
    const res = await api().updateVmHardware(vmId, payload as UpdateVmHardwareDto)
    return res.data
  }

  return {
    images,
    quota,
    loading,
    error,
    isos,
    diskImages,
    busy,
    mountedByVm,
    fetchImages,
    refreshQuietly,
    fetchQuota,
    getImage,
    updateImage,
    getCloudInit,
    updateCloudInit,
    validateCloudInit,
    renewImage,
    deleteImage,
    getTargets,
    attach,
    detach,
    reinstallFromImage,
    getHardwareOptions,
    getVmHardwareConfig,
    updateVmHardware,
  }
})
